(() => {
  'use strict';
  const totals = items => {
    const result = new Map();
    for (const item of items) result.set(String(item.id), (result.get(String(item.id)) || 0) + Number(item.quantity));
    return result;
  };
  const changes = (kind, body, items) => {
    const result = items.map(item => ({ ...item }));
    const quantity = value => {
      const number = Number(value);
      if (!Number.isSafeInteger(number) || number < 0) throw new Error('invalid_quantity');
      return number;
    };
    const locate = id => {
      const exact = result.findIndex(item => item.key === String(id));
      if (exact >= 0) return exact;
      return result.findIndex(item => String(item.id) === String(id));
    };
    if (kind === 'add') {
      for (const item of body.items || [body]) {
        if (!/^\d+$/.test(String(item.id))) throw new Error('missing_variant');
        result.push({ id: String(item.id), quantity: quantity(item.quantity ?? 1) });
      }
    } else if (kind === 'change') {
      const index = body.line !== undefined ? Number(body.line) - 1 : locate(body.id);
      if (!Number.isInteger(index) || !result[index]) throw new Error('missing_line');
      result[index].quantity = quantity(body.quantity);
    } else if (kind === 'update' && body.updates) {
      for (const [id, value] of Object.entries(body.updates)) {
        const index = Array.isArray(body.updates) ? Number(id) : locate(id);
        if (index < 0 || !result[index]) {
          if (quantity(value) > 0 && !Array.isArray(body.updates)) result.push({ id, quantity: quantity(value) });
          else if (quantity(value) > 0) throw new Error('missing_line');
        } else result[index].quantity = quantity(value);
      }
    }
    return result;
  };
  const violations = (before, after, rules, now = Date.now(), checkout = false) => {
    const old = totals(before), next = totals(after), errors = [];
    for (const [id, amount] of next) {
      if (amount <= 0 || (!checkout && amount <= (old.get(id) || 0))) continue;
      const rule = rules.get(id);
      if (!rule || !Number.isFinite(rule.maximum) || rule.maximum < 0) throw new Error('missing_stock');
      const maximum = rule.linked && now >= rule.expires * 1000 ? Math.min(rule.maximum, rule.own) : rule.maximum;
      if (amount > maximum) errors.push({ id, maximum, inCart: old.get(id) || 0, title: rule.title, lastStock: rule.last_stock });
    }
    return errors;
  };
  if (typeof document === 'undefined') {
    if (typeof module !== 'undefined') module.exports = { totals, changes, violations };
    return;
  }
  const config = JSON.parse(document.querySelector('[data-fxl-stock-config]').textContent);
  config.root = config.root.replace(/\/?$/, '/');
  const handles = new Map();
  const limits = new Map();
  let tail = Promise.resolve();
  const t = (key, values = {}) => {
    let message = config[key];
    for (const [name, value] of Object.entries(values)) message = message.replaceAll('[' + name + ']', String(value));
    return message;
  };
  const limitMessage = (rule, maximum, inCart) => {
    const values = { title: rule.title, maximum, in_cart: inCart };
    if (!rule.last_stock) return t('limit', values);
    let message = rule.last_stock;
    for (const [name, value] of Object.entries(values)) message = message.replaceAll('[' + name + ']', String(value));
    return message;
  };
  const register = pairs => { for (const [id, handle] of Object.entries(pairs || {})) if (handle) handles.set(String(id), handle); };
  const scan = () => {
    for (const node of document.querySelectorAll('[data-fxl-stock-product]')) {
      if (node.dataset.registered) continue;
      try { const product = JSON.parse(node.textContent); for (const id of product.ids) handles.set(String(id), product.handle); if (!limits.has(product.rule.id)) limits.set(product.rule.id, product.rule); node.dataset.registered = 'true'; } catch {}
    }
  };
  const state = async (path, variant) => {
    const url = new URL(path, location.origin);
    url.searchParams.set('section_id', 'fxl-stock-state');
    url.searchParams.set('_fxl_stock', String(Date.now()));
    if (variant) url.searchParams.set('variant', variant);
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(12000) });
    if (!response.ok) throw new Error('stock_unavailable');
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const data = JSON.parse(html.querySelector('[data-fxl-stock-state]')?.textContent || 'null');
    if (data?.version !== 1 || !Array.isArray(data.items)) throw new Error('stock_unavailable');
    if (data.line_count !== data.items.length || data.item_count !== data.items.reduce((sum, item) => sum + item.quantity, 0)) throw new Error('incomplete_cart');
    return data;
  };
  const decode = body => {
    if (typeof body === 'string') return JSON.parse(body);
    if (!(body instanceof FormData) && !(body instanceof URLSearchParams)) throw new Error('unsupported_body');
    const data = {}, items = [], updates = {};
    for (const [name, value] of body.entries()) {
      const item = name.match(/^items\[(\d+)\]\[(id|quantity)\]$/), update = name.match(/^updates\[(.*?)\]$/);
      if (item) { items[Number(item[1])] ||= {}; items[Number(item[1])][item[2]] = value; }
      else if (update) {
        if (!update[1]) { data.updates ||= []; data.updates.push(value); }
        else updates[update[1]] = value;
      } else data[name] = value;
    }
    if (items.length) data.items = items.filter(Boolean);
    if (Object.keys(updates).length) data.updates = updates;
    return data;
  };
  const errorResponse = description => new Response(JSON.stringify({ status: 422, description, message: description }), { status: 422, headers: { 'Content-Type': 'application/json' } });
  const check = async (kind, body) => {
    scan();
    const cart = await state(config.root + 'cart');
    const after = kind === 'checkout' ? cart.items : changes(kind, body, cart.items);
    const rules = new Map(cart.items.map(item => [String(item.id), item.rule]));
    const missing = [...totals(after)].filter(([id, quantity]) => quantity > 0 && !rules.has(id));
    for (let start = 0; start < missing.length; start += 4) {
      await Promise.all(missing.slice(start, start + 4).map(async ([id]) => {
        const handle = handles.get(id);
        if (!handle) throw new Error('missing_product');
        const data = await state(config.root + 'products/' + encodeURIComponent(handle), id);
        if (String(data.product?.id) !== id) throw new Error('missing_variant');
        rules.set(id, data.product);
      }));
    }
    const errors = violations(cart.items, after, rules, Date.now(), kind === 'checkout');
    for (const [id, rule] of rules) limits.set(id, rule);
    if (errors.length) {
      const error = errors[0];
      return limitMessage({ title: error.title, last_stock: error.lastStock }, error.maximum, error.inCart);
    }
    return '';
  };
  const queue = task => {
    const run = () => navigator.locks ? navigator.locks.request('fxl-supplier-cart', task) : task();
    const result = tail.then(run);
    tail = result.catch(() => {});
    return result;
  };
  const request = (url, options = {}) => {
    const target = new URL(url, location.origin);
    const kind = target.pathname.match(/\/cart\/(add|change|update)(?:\.js)?\/?$/)?.[1];
    if (!kind || target.origin !== location.origin || (options.method || 'GET').toUpperCase() !== 'POST') return fetch(url, options);
    return queue(async () => {
      let body;
      try {
        body = decode(options.body);
        if (kind !== 'update' || body.updates) {
          const message = await check(kind, body);
          if (message) return errorResponse(message);
        }
      } catch { return errorResponse(t('unavailable')); }
      const response = await fetch(url, options);
      await response.clone().text();
      return response;
    });
  };
  const checkout = () => queue(async () => {
    let message;
    try { message = await check('checkout', {}); } catch { throw new Error(t('unavailable')); }
    if (message) throw new Error(message);
  });
  window.FXLStock = { request, checkout, register };
  const refreshInputs = () => {
    scan();
    const cart = window.FXLCartPage?.state || window.FXLCartDrawer?.state;
    const amounts = totals(cart?.items || []);
    for (const form of document.querySelectorAll('form[action*="/cart/add"]')) {
      const id = String(form.elements.id?.value || ''), rule = limits.get(id);
      const field = form.querySelector('input[name="quantity"][type="number"]');
      if (!field || !rule) continue;
      const max = rule.linked && Date.now() >= rule.expires * 1000 ? Math.min(rule.maximum, rule.own) : rule.maximum;
      const remaining = cart ? Math.max(0, max - (amounts.get(id) || 0)) : Math.min(max, Number(field.max || max));
      field.max = String(remaining);
      field.setCustomValidity(remaining < (Number(field.min) || 1) || Number(field.value) > remaining
        ? limitMessage(rule, max, cart ? amounts.get(id) || 0 : Math.max(0, max - remaining))
        : '');
    }
  };
  document.addEventListener('cart:updated', refreshInputs);
  document.addEventListener('shopify:section:load', refreshInputs);
  document.addEventListener('input', event => {
    if (event.target.matches('form[action*="/cart/add"] input[name="quantity"]')) refreshInputs();
  });
  scan();
})();
